import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { usersAPI, settingsAPI, rbacAPI, payrollAPI, getErrorMessage } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { buildPermissionModules, enrichProfilesWithModuleSummary, normalizeProfilePermissions } from '@/lib/profilePermissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ExportColumnsDialog } from '@/components/ExportColumnsDialog';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowRight,
  BriefcaseBusiness,
  Download,
  Link2,
  Loader2,
  Pencil,
  Power,
  PowerOff,
  Plus,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  UserCog,
  Users,
  Eye,
  History,
  FileSpreadsheet,
} from 'lucide-react';

const ADMIN_CENTER_TABS = ['users', 'employees', 'roles', 'gaps'];
const ADMIN_CENTER_CORE_CACHE_KEY = 'admin-center-core-cache-v2';
const ADMIN_CENTER_DEFERRED_CACHE_KEY = 'admin-center-deferred-cache-v2';
const COMMERCIAL_PIN_ROLES = ['admin', 'store_manager', 'supervisor'];
const MARITAL_STATUS_OPTIONS = ['Solteiro(a)', 'Casado(a)', 'União de Facto', 'Divorciado(a)', 'Viúvo(a)', 'Separado(a)'];
const GENDER_OPTIONS = ['Masculino', 'Feminino', 'Outro', 'Prefiro não informar'];
const DOCUMENT_IDENTIFICATION_TYPE_OPTIONS = [
  'D.I.R.E.',
  'CERTIDÃO DE NASCIMENTO',
  'BILHETE DE IDENTIDADE',
  'CÉDULA PESSOAL',
  'PASSAPORTE',
  'ASSENTO DE NASCIMENTO',
  'BOLETIM DE NASCIMENTO',
  'TALÃO DO BILHETE DE IDENTIDADE',
  'DESCONHECIDO',
];

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700 border-green-300',
  inactive: 'bg-slate-100 text-slate-700 border-slate-300',
  suspended: 'bg-red-100 text-red-700 border-red-300',
};

const emptyUserForm = {
  full_name: '',
  email: '',
  phone_number: '',
  role: 'staff',
  selected_profile: 'base:staff',
  store_location: '',
  status: 'active',
  password: '',
  commercial_authorization_pin: '',
  clear_commercial_authorization_pin: false,
  create_employee_twin: false,
};

const emptyEmployeeForm = {
  employee_number: '',
  name: '',
  selected_profile: 'base:staff',
  phone: '',
  email: '',
  marital_status: '',
  gender: '',
  father_name: '',
  mother_name: '',
  document_ident_number: '',
  document_ident_type: '',
  document_issuer_service: '',
  bi_number: '',
  can_sell: true,
  is_active: true,
  create_user_twin: false,
  user_store_location: '',
  user_password: '',
  user_base_salary: '',
};

const emptyRoleForm = {
  name: '',
  description: '',
  color: '#1d4ed8',
  permissions: [],
};

const USER_EXPORTABLE_COLUMNS = [
  { key: 'full_name', label: 'Nome' },
  { key: 'email', label: 'Email' },
  { key: 'phone_number', label: 'Telefone' },
  { key: 'role', label: 'Perfil Base' },
  { key: 'custom_role_name', label: 'Perfil Configurável' },
  { key: 'status', label: 'Estado' },
  { key: 'store_location', label: 'Loja' },
];

const CompactTabSkeleton = ({ rows = 3 }) => (
  <div className="space-y-2" data-testid="admin-center-compact-skeleton">
    {Array.from({ length: rows }).map((_, index) => (
      <div key={`admin-center-skeleton-${index}`} className="animate-pulse rounded-2xl border border-slate-200 bg-slate-50 p-3">
        <div className="h-3 w-40 rounded bg-slate-200" />
        <div className="mt-2 h-2 w-64 rounded bg-slate-200" />
        <div className="mt-3 grid gap-2 md:grid-cols-3">
          <div className="h-8 rounded bg-slate-200" />
          <div className="h-8 rounded bg-slate-200" />
          <div className="h-8 rounded bg-slate-200" />
        </div>
      </div>
    ))}
  </div>
);

const EMPLOYEE_EXPORTABLE_COLUMNS = [
  { key: 'employee_number', label: 'Nº Colaborador' },
  { key: 'name', label: 'Nome' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Telefone' },
  { key: 'role', label: 'Cargo / Perfil' },
  { key: 'is_active', label: 'Activo' },
  { key: 'can_sell', label: 'Pode vender' },
  { key: 'user_id', label: 'ID Utilizador' },
  { key: 'created_at', label: 'Criado Em' },
];

const downloadBlobFile = (blob, filename) => {
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
};

export default function AdminCenterPage() {
  const { hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const canAccess = hasRole(['admin', 'store_manager']);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [baseRoles, setBaseRoles] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [activeTab, setActiveTab] = useState(ADMIN_CENTER_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'users');
  const [directoryQuery, setDirectoryQuery] = useState('');
  const [directoryStatusFilter, setDirectoryStatusFilter] = useState('all');
  const [linkageFilter, setLinkageFilter] = useState('all');
  const [sortMode, setSortMode] = useState('name-asc');
  const [quickActionKey, setQuickActionKey] = useState(null);
  const [tabLoading, setTabLoading] = useState({ users: false, employees: false, roles: false, gaps: false });
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [userExportDialogOpen, setUserExportDialogOpen] = useState(false);
  const [employeeExportDialogOpen, setEmployeeExportDialogOpen] = useState(false);
  const [userExportColumns, setUserExportColumns] = useState(USER_EXPORTABLE_COLUMNS.map((column) => column.key));
  const [employeeExportColumns, setEmployeeExportColumns] = useState(EMPLOYEE_EXPORTABLE_COLUMNS.map((column) => column.key));
  const [userAuditTrail, setUserAuditTrail] = useState([]);
  const [userAuditDialogOpen, setUserAuditDialogOpen] = useState(false);
  const [selectedUserAudit, setSelectedUserAudit] = useState(null);
  const [selectedUserAuditLogs, setSelectedUserAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteEntityDialog, setDeleteEntityDialog] = useState({ open: false, type: null, item: null });

  const [editingUser, setEditingUser] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [resetTargetUser, setResetTargetUser] = useState(null);

  const [userForm, setUserForm] = useState(emptyUserForm);
  const [employeeForm, setEmployeeForm] = useState(emptyEmployeeForm);
  const [roleForm, setRoleForm] = useState(emptyRoleForm);
  const [passwordForm, setPasswordForm] = useState({ new_password: '', justification: '' });

  const userFormSelectedProfile = useMemo(() => parseUserProfileValue(userForm.selected_profile), [userForm.selected_profile]);
  const userFormResolvedBaseRole = useMemo(
    () => (userFormSelectedProfile.type === 'base' ? userFormSelectedProfile.key : (editingUser?.role || userForm.role || 'staff')),
    [editingUser?.role, userForm.role, userFormSelectedProfile],
  );
  const userCanConfigureCommercialPin = useMemo(
    () => COMMERCIAL_PIN_ROLES.includes(userFormResolvedBaseRole),
    [userFormResolvedBaseRole],
  );

  const permissionModules = useMemo(() => buildPermissionModules(permissions), [permissions]);
  const allPermissionSlugs = useMemo(() => permissions.map((permission) => permission.slug).filter(Boolean), [permissions]);
  const baseRoleLabelMap = useMemo(() => new Map(baseRoles.map((role) => [role.key, role.label])), [baseRoles]);
  const unifiedUserProfileOptions = useMemo(() => ([
    ...baseRoles.map((role) => ({ value: `base:${role.key}`, label: role.label, type: 'base' })),
    ...roles.map((role) => ({ value: `custom:${role.id}`, label: role.name, type: 'custom' })),
  ]), [baseRoles, roles]);
  const unifiedEmployeeProfileOptions = useMemo(() => ([
    ...baseRoles.map((role) => ({
      value: `base:${role.key}`,
      label: role.label,
      profileKey: role.key,
      profileSource: 'base_role',
      profileId: null,
      type: 'base',
    })),
    ...roles.map((role) => ({
      value: `custom:${role.id}`,
      label: role.name,
      profileKey: role.key || role.id,
      profileSource: 'custom_role',
      profileId: role.id,
      type: 'custom',
    })),
  ]), [baseRoles, roles]);

  const resolveUserProfileValue = (user) => user?.custom_role_id ? `custom:${user.custom_role_id}` : `base:${user?.role || 'staff'}`;
  const resolveUserProfileLabel = (user) => ((user?.custom_role_id || user?.custom_role_key) ? user?.custom_role_name : null)
    || user?.job_title
    || baseRoleLabelMap.get(user?.role)
    || user?.role
    || 'Sem perfil';
  function parseUserProfileValue(value) {
    return value?.startsWith('custom:')
      ? { type: 'custom', id: value.replace('custom:', '') }
      : { type: 'base', key: value?.replace('base:', '') || 'staff' };
  }
  const getEmployeeProfileOption = useCallback((value) => {
    return unifiedEmployeeProfileOptions.find((profile) => profile.value === value) || unifiedEmployeeProfileOptions[0] || {
      value: 'base:staff',
      label: 'Staff',
      profileKey: 'staff',
      profileSource: 'base_role',
      profileId: null,
      type: 'base',
    };
  }, [unifiedEmployeeProfileOptions]);
  const resolveEmployeeProfileValue = useCallback((employee, linkedUser = null) => {
    if (linkedUser?.custom_role_id) {
      return `custom:${linkedUser.custom_role_id}`;
    }
    if (linkedUser?.role) {
      return `base:${linkedUser.role}`;
    }
    if (employee?.profile_role_source === 'custom_role') {
      if (employee?.profile_role_id) {
        return `custom:${employee.profile_role_id}`;
      }
      const matchedCustomRole = roles.find((role) => role.key === employee?.profile_role_key || role.id === employee?.profile_role_key);
      if (matchedCustomRole) {
        return `custom:${matchedCustomRole.id}`;
      }
    }
    return `base:${employee?.profile_role_key || 'staff'}`;
  }, [roles]);

  const persistAdminCenterCache = (cacheKey, value) => {
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(value));
    } catch {
      // ignore cache issues
    }
  };

  const readAdminCenterCache = (cacheKey) => {
    try {
      const rawValue = sessionStorage.getItem(cacheKey);
      return rawValue ? JSON.parse(rawValue) : null;
    } catch {
      return null;
    }
  };

  const employeeByEmail = useMemo(() => {
    return new Map(
      employees
        .filter((item) => item.email)
        .map((item) => [String(item.email).trim().toLowerCase(), item])
    );
  }, [employees]);

  const employeeByUserId = useMemo(() => {
    return new Map(
      employees
        .filter((item) => item.user_id)
        .map((item) => [item.user_id, item])
    );
  }, [employees]);

  const userByEmail = useMemo(() => {
    return new Map(
      users
        .filter((item) => item.email)
        .map((item) => [String(item.email).trim().toLowerCase(), item])
    );
  }, [users]);

  const userById = useMemo(() => new Map(users.map((item) => [item.id, item])), [users]);

  const loadDeferredData = useCallback(async (targetTab = activeTab, { force = false } = {}) => {
    const tasks = [];
    setTabLoading((current) => ({ ...current, [targetTab]: true }));

    if ((targetTab === 'roles' || force) && (!permissions.length || !assignments.length || force)) {
      tasks.push(
        Promise.all([rbacAPI.listPermissions(), rbacAPI.listUserAssignments()]).then(([permissionsRes, assignmentsRes]) => {
          setPermissions((permissionsRes.data || []).filter((item) => !item.is_deleted));
          setAssignments(assignmentsRes.data || []);
          persistAdminCenterCache(ADMIN_CENTER_DEFERRED_CACHE_KEY, {
            ...(readAdminCenterCache(ADMIN_CENTER_DEFERRED_CACHE_KEY) || {}),
            permissions: (permissionsRes.data || []).filter((item) => !item.is_deleted),
            assignments: assignmentsRes.data || [],
          });
        })
      );
    }

    if ((targetTab === 'gaps' || force) && (!coverage || force)) {
      tasks.push(
        rbacAPI.getCoverage().then((coverageRes) => {
          setCoverage(coverageRes.data || null);
          persistAdminCenterCache(ADMIN_CENTER_DEFERRED_CACHE_KEY, {
            ...(readAdminCenterCache(ADMIN_CENTER_DEFERRED_CACHE_KEY) || {}),
            coverage: coverageRes.data || null,
          });
        })
      );
    }

    if ((targetTab === 'users' || force) && (!userAuditTrail.length || force)) {
      tasks.push(
        usersAPI.getAllAuditLogs(20).then((auditTrailRes) => {
          setUserAuditTrail(auditTrailRes?.data || []);
          persistAdminCenterCache(ADMIN_CENTER_DEFERRED_CACHE_KEY, {
            ...(readAdminCenterCache(ADMIN_CENTER_DEFERRED_CACHE_KEY) || {}),
            userAuditTrail: auditTrailRes?.data || [],
          });
        })
      );
    }

    if (!tasks.length) {
      setTabLoading((current) => ({ ...current, [targetTab]: false }));
      return;
    }
    await Promise.allSettled(tasks);
    setTabLoading((current) => ({ ...current, [targetTab]: false }));
  }, [activeTab, assignments.length, coverage, permissions.length, userAuditTrail.length]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const coreCache = readAdminCenterCache(ADMIN_CENTER_CORE_CACHE_KEY);
      const deferredCache = readAdminCenterCache(ADMIN_CENTER_DEFERRED_CACHE_KEY);
      if (coreCache) {
        setUsers(coreCache.users || []);
        setEmployees(coreCache.employees || []);
        setRoles(coreCache.roles || []);
        setLocations(coreCache.locations || []);
        setBaseRoles(coreCache.baseRoles || []);
        setStatuses(coreCache.statuses || []);
        setLoading(false);
      }
      if (deferredCache) {
        setPermissions(deferredCache.permissions || []);
        setAssignments(deferredCache.assignments || []);
        setCoverage(deferredCache.coverage || null);
        setUserAuditTrail(deferredCache.userAuditTrail || []);
      }

      const [
        usersRes,
        employeesRes,
        rolesRes,
        locationsRes,
        baseRolesRes,
        statusesRes,
      ] = await Promise.all([
        usersAPI.list(),
        settingsAPI.listEmployees(false),
        rbacAPI.listRoles(false),
        settingsAPI.listLocations(false),
        rbacAPI.listBaseRoles(),
        usersAPI.getStatuses(),
      ]);

      setUsers(usersRes.data || []);
      setEmployees((employeesRes.data || []).filter((item) => !item.is_deleted));
      setRoles((rolesRes.data || []).filter((item) => !item.is_deleted));
      setLocations((locationsRes.data || []).filter((item) => !item.is_deleted));
      setBaseRoles(baseRolesRes.data || []);
      setStatuses(statusesRes.data || []);
      persistAdminCenterCache(ADMIN_CENTER_CORE_CACHE_KEY, {
        users: usersRes.data || [],
        employees: (employeesRes.data || []).filter((item) => !item.is_deleted),
        roles: (rolesRes.data || []).filter((item) => !item.is_deleted),
        locations: (locationsRes.data || []).filter((item) => !item.is_deleted),
        baseRoles: baseRolesRes.data || [],
        statuses: statusesRes.data || [],
      });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar Centro Administrativo'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canAccess) {
      loadData();
    }
  }, [canAccess, loadData]);

  useEffect(() => {
    if (canAccess && !loading) {
      loadDeferredData(activeTab).catch(() => null);
    }
  }, [activeTab, canAccess, loading, loadDeferredData]);

  const insights = useMemo(() => {
    const employeeEmailMap = new Map(
      employees
        .filter((item) => item.email)
        .map((item) => [String(item.email).trim().toLowerCase(), item])
    );
    const userEmailMap = new Map(
      users
        .filter((item) => item.email)
        .map((item) => [String(item.email).trim().toLowerCase(), item])
    );

    const linkedUsers = users.filter((item) => employeeEmailMap.has(String(item.email || '').trim().toLowerCase()));
    const employeesWithoutAccount = employees.filter((item) => !userEmailMap.has(String(item.email || '').trim().toLowerCase()));
    const usersWithoutEmployee = users.filter((item) => !employeeEmailMap.has(String(item.email || '').trim().toLowerCase()));
    const usersWithoutCustomRole = users.filter((item) => !item.custom_role_id);
    const rolesWithoutPermissions = [...roles, ...baseRoles].filter((item) => !((item.permissions || []).length) && !((item.permissions || []).includes('*')));

    return {
      linkedUsers,
      employeesWithoutAccount,
      usersWithoutEmployee,
      usersWithoutCustomRole,
      rolesWithoutPermissions,
    };
  }, [employees, roles, baseRoles, users]);

  const matchedEmployeeForUserForm = useMemo(() => {
    return employees.find((item) => String(item.email || '').trim().toLowerCase() === String(userForm.email || '').trim().toLowerCase());
  }, [employees, userForm.email]);

  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    if (requestedTab && ADMIN_CENTER_TABS.includes(requestedTab) && requestedTab !== activeTab) {
      setActiveTab(requestedTab);
    }
  }, [searchParams, activeTab]);

  const handleAdminCenterTabChange = (nextTab) => {
    const safeTab = ADMIN_CENTER_TABS.includes(nextTab) ? nextTab : 'users';
    setActiveTab(safeTab);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', safeTab);
    setSearchParams(nextParams, { replace: true });
  };

  const matchedUserForEmployeeForm = useMemo(() => {
    return users.find((item) => String(item.email || '').trim().toLowerCase() === String(employeeForm.email || '').trim().toLowerCase());
  }, [employeeForm.email, users]);

  const getLinkedEmployeeForUser = useCallback((user) => {
    return employeeByUserId.get(user.id) || employeeByEmail.get(String(user.email || '').trim().toLowerCase()) || null;
  }, [employeeByEmail, employeeByUserId]);

  const getLinkedUserForEmployee = useCallback((employee) => {
    return userById.get(employee.user_id) || userByEmail.get(String(employee.email || '').trim().toLowerCase()) || null;
  }, [userByEmail, userById]);

  const searchTerm = useMemo(() => directoryQuery.trim().toLowerCase(), [directoryQuery]);

  const filteredUsers = useMemo(() => {
    return [...users].sort((left, right) => {
      if (sortMode === 'name-desc') return String(right.full_name || '').localeCompare(String(left.full_name || ''), 'pt');
      if (sortMode === 'collab-asc') return String(left.employee_number || '999999').localeCompare(String(right.employee_number || '999999'), 'pt', { numeric: true });
      if (sortMode === 'collab-desc') return String(right.employee_number || '000000').localeCompare(String(left.employee_number || '000000'), 'pt', { numeric: true });
      return String(left.full_name || '').localeCompare(String(right.full_name || ''), 'pt');
    }).filter((item) => {
      const linkedEmployee = getLinkedEmployeeForUser(item);
      const matchesQuery = !searchTerm || [item.full_name, item.email, item.role, item.store_location, item.custom_role_name, item.employee_number]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(searchTerm));
      const matchesStatus = directoryStatusFilter === 'all' || item.status === directoryStatusFilter;
      const matchesLinkage = linkageFilter === 'all'
        || (linkageFilter === 'linked' && linkedEmployee)
        || (linkageFilter === 'unlinked' && !linkedEmployee)
        || (linkageFilter === 'persisted' && linkedEmployee?.user_id === item.id)
        || (linkageFilter === 'role-gap' && !item.custom_role_id);
      return matchesQuery && matchesStatus && matchesLinkage;
    });
  }, [users, searchTerm, directoryStatusFilter, linkageFilter, getLinkedEmployeeForUser, sortMode]);

  const filteredEmployees = useMemo(() => {
    return [...employees].sort((left, right) => {
      if (sortMode === 'name-desc') return String(right.name || '').localeCompare(String(left.name || ''), 'pt');
      if (sortMode === 'collab-asc') return String(left.employee_number || '999999').localeCompare(String(right.employee_number || '999999'), 'pt', { numeric: true });
      if (sortMode === 'collab-desc') return String(right.employee_number || '000000').localeCompare(String(left.employee_number || '000000'), 'pt', { numeric: true });
      return String(left.name || '').localeCompare(String(right.name || ''), 'pt');
    }).filter((item) => {
      const linkedUser = getLinkedUserForEmployee(item);
      const matchesQuery = !searchTerm || [item.name, item.email, item.profile_role_label, item.role, item.phone, item.employee_number]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(searchTerm));
      const employeeStatus = item.is_active ? 'active' : 'inactive';
      const matchesStatus = directoryStatusFilter === 'all' || employeeStatus === directoryStatusFilter;
      const matchesLinkage = linkageFilter === 'all'
        || (linkageFilter === 'linked' && linkedUser)
        || (linkageFilter === 'unlinked' && !linkedUser)
        || (linkageFilter === 'persisted' && !!item.user_id)
        || linkageFilter === 'role-gap';
      return matchesQuery && matchesStatus && matchesLinkage;
    });
  }, [employees, searchTerm, directoryStatusFilter, linkageFilter, getLinkedUserForEmployee, sortMode]);

  useEffect(() => {
    setSelectedUserIds((current) => current.filter((id) => filteredUsers.some((item) => item.id === id)));
  }, [filteredUsers]);

  useEffect(() => {
    setSelectedEmployeeIds((current) => current.filter((id) => filteredEmployees.some((item) => item.id === id)));
  }, [filteredEmployees]);

  const combinedRoles = useMemo(() => enrichProfilesWithModuleSummary([
    ...baseRoles.map((role) => ({ ...role, type: 'base' })),
    ...roles.map((role) => ({ ...role, type: role.type || 'custom' })),
  ], permissions), [baseRoles, roles, permissions]);

  const filteredRoles = useMemo(() => {
    return combinedRoles.filter((role) => {
      const matchesQuery = !searchTerm || [role.name, role.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(searchTerm));
      const matchesLinkage = linkageFilter === 'all' || linkageFilter === 'role-gap'
        ? (linkageFilter !== 'role-gap' || (!role.isFullAccess && !(role.normalizedPermissions || []).length))
        : true;
      return matchesQuery && matchesLinkage;
    });
  }, [combinedRoles, searchTerm, linkageFilter]);

  const cockpitSummary = useMemo(() => ({
    linkedPersistent: employees.filter((item) => item.user_id).length,
    suspendedUsers: users.filter((item) => item.status === 'suspended').length,
    inactiveEmployees: employees.filter((item) => item.is_active === false).length,
  }), [employees, users]);

  const usersSummary = useMemo(() => ({
    active: users.filter((item) => item.status === 'active').length,
    suspended: users.filter((item) => item.status === 'suspended').length,
    withoutCustomRole: users.filter((item) => !item.custom_role_id).length,
    audits: userAuditTrail.length,
  }), [users, userAuditTrail]);

  const allVisibleUsersSelected = filteredUsers.length > 0 && filteredUsers.every((item) => selectedUserIds.includes(item.id));
  const allVisibleEmployeesSelected = filteredEmployees.length > 0 && filteredEmployees.every((item) => selectedEmployeeIds.includes(item.id));

  const openCreateUser = () => {
    setEditingUser(null);
    setUserForm(emptyUserForm);
    setUserDialogOpen(true);
  };

  const openCreateUserFromEmployee = (employee) => {
    setEditingUser(null);
    setUserForm({
      ...emptyUserForm,
      full_name: employee.name || '',
      email: employee.email || '',
      phone_number: employee.phone || '',
      role: employee.profile_role_key || 'staff',
      selected_profile: resolveEmployeeProfileValue(employee, getLinkedUserForEmployee(employee)),
    });
    setUserDialogOpen(true);
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      full_name: user.full_name || '',
      email: user.email || '',
      phone_number: user.phone_number || '',
      role: user.role || 'staff',
      selected_profile: resolveUserProfileValue(user),
      store_location: user.store_location || '',
      status: user.status || 'active',
      password: '',
      commercial_authorization_pin: '',
      clear_commercial_authorization_pin: false,
    });
    setUserDialogOpen(true);
  };

  const saveUser = async () => {
    try {
      setSaving(true);
      const selectedProfile = userFormSelectedProfile;
      const resolvedBaseRole = userFormResolvedBaseRole;

      if (editingUser) {
        const updatePayload = {
          full_name: userForm.full_name,
          phone_number: userForm.phone_number,
          role: resolvedBaseRole,
          store_location: userForm.store_location || null,
          status: userForm.status,
          commercial_authorization_pin: userCanConfigureCommercialPin ? (userForm.commercial_authorization_pin?.trim() || undefined) : undefined,
          clear_commercial_authorization_pin: userCanConfigureCommercialPin ? Boolean(userForm.clear_commercial_authorization_pin) : false,
          justification: 'Edição inline via Centro Administrativo',
        };

        const needsUserUpdate = (
          editingUser.full_name !== updatePayload.full_name
          || (editingUser.phone_number || '') !== (updatePayload.phone_number || '')
          || (editingUser.role || 'staff') !== updatePayload.role
          || (editingUser.store_location || null) !== updatePayload.store_location
          || (editingUser.status || 'active') !== updatePayload.status
        );

        if (needsUserUpdate) {
          await usersAPI.update(editingUser.id, updatePayload);
        }

        if (selectedProfile.type === 'custom') {
          await rbacAPI.assignRoleToUser(editingUser.id, selectedProfile.id, 'Sincronização do perfil via edição de utilizador');
        } else if (editingUser.custom_role_id) {
          await rbacAPI.assignRoleToUser(editingUser.id, null, 'Remoção do perfil criado via edição de utilizador');
        }

        toast.success('Utilizador actualizado');
      } else {
        const createdUserResponse = await usersAPI.create({
          full_name: userForm.full_name,
          email: userForm.email,
          phone_number: userForm.phone_number,
          role: resolvedBaseRole,
          store_location: userForm.store_location || null,
          password: userForm.password,
          commercial_authorization_pin: userCanConfigureCommercialPin ? (userForm.commercial_authorization_pin?.trim() || undefined) : undefined,
        });

        if (selectedProfile.type === 'custom' && createdUserResponse?.data?.id) {
          await rbacAPI.assignRoleToUser(createdUserResponse.data.id, selectedProfile.id, 'Atribuição inicial de perfil via criação de utilizador');
        }

        toast.success('Utilizador criado');
      }
      setUserDialogOpen(false);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao guardar utilizador'));
    } finally {
      setSaving(false);
    }
  };

  const openEditEmployee = (employee) => {
    const linkedUser = getLinkedUserForEmployee(employee);
    setEditingEmployee(employee);
    setEmployeeForm({
      employee_number: employee.employee_number || '',
      name: employee.name || '',
      selected_profile: resolveEmployeeProfileValue(employee, linkedUser),
      phone: employee.phone || '',
      email: employee.email || '',
      marital_status: employee.marital_status || linkedUser?.marital_status || '',
      gender: employee.gender || linkedUser?.gender || '',
      father_name: employee.father_name || linkedUser?.father_name || '',
      mother_name: employee.mother_name || linkedUser?.mother_name || '',
      document_ident_number: employee.document_ident_number || employee.bi_number || linkedUser?.document_ident_number || linkedUser?.bi_number || '',
      document_ident_type: employee.document_ident_type || linkedUser?.document_ident_type || '',
      document_issuer_service: employee.document_issuer_service || linkedUser?.document_issuer_service || '',
      bi_number: employee.bi_number || linkedUser?.bi_number || '',
      can_sell: employee.can_sell ?? true,
      is_active: employee.is_active ?? true,
    });
    setEmployeeDialogOpen(true);
  };

  const saveEmployee = async () => {
    try {
      setSaving(true);
      const selectedProfile = getEmployeeProfileOption(employeeForm.selected_profile);
      const linkedUser = editingEmployee ? getLinkedUserForEmployee(editingEmployee) : matchedUserForEmployeeForm;
      const employeePayload = {
        employee_number: employeeForm.employee_number,
        name: employeeForm.name,
        phone: employeeForm.phone,
        email: employeeForm.email,
        marital_status: employeeForm.marital_status || null,
        gender: employeeForm.gender || null,
        father_name: employeeForm.father_name || null,
        mother_name: employeeForm.mother_name || null,
        document_ident_number: employeeForm.bi_number || employeeForm.document_ident_number || null,
        document_ident_type: employeeForm.document_ident_type || null,
        document_issuer_service: employeeForm.document_issuer_service || null,
        bi_number: employeeForm.bi_number || null,
        can_sell: employeeForm.can_sell,
        is_active: employeeForm.is_active,
        profile_role_key: selectedProfile.profileKey,
        profile_role_source: selectedProfile.profileSource,
        profile_role_id: selectedProfile.profileId,
        profile_role_label: selectedProfile.label,
      };

      if (linkedUser?.id) {
        if (selectedProfile.type === 'base') {
          if ((linkedUser.role || 'staff') !== selectedProfile.profileKey) {
            await usersAPI.update(linkedUser.id, {
              role: selectedProfile.profileKey,
          justification: 'Sincronização do perfil via cadastro de colaborador',
            });
          }
          if (linkedUser.custom_role_id || linkedUser.custom_role_key || linkedUser.custom_role_name) {
            await rbacAPI.assignRoleToUser(linkedUser.id, null, 'Limpeza do perfil customizado via cadastro de colaborador');
          }
        } else if (selectedProfile.profileId && linkedUser.custom_role_id !== selectedProfile.profileId) {
          await rbacAPI.assignRoleToUser(linkedUser.id, selectedProfile.profileId, 'Sincronização do perfil via cadastro de colaborador');
        }
      }

      if (editingEmployee) {
        await settingsAPI.updateEmployee(editingEmployee.id, employeePayload, 'Edição inline via Centro Administrativo');
        toast.success('Colaborador actualizado');
      } else {
        await settingsAPI.createEmployee(employeePayload);
        if (employeeForm.create_user_twin && !matchedUserForEmployeeForm) {
          const createdUserResponse = await usersAPI.create({
            full_name: employeeForm.name,
            email: employeeForm.email,
            phone_number: employeeForm.phone,
            role: selectedProfile.type === 'base' ? selectedProfile.profileKey : 'staff',
            store_location: employeeForm.user_store_location || null,
            password: employeeForm.user_password,
            base_salary: employeeForm.user_base_salary ? parseFloat(employeeForm.user_base_salary) : 0,
          });
          if (createdUserResponse?.data?.id) {
            await payrollAPI.updateEmployeeDossier(createdUserResponse.data.id, {
              marital_status: employeeForm.marital_status || null,
              gender: employeeForm.gender || null,
              father_name: employeeForm.father_name || null,
              mother_name: employeeForm.mother_name || null,
              document_ident_number: employeeForm.bi_number || employeeForm.document_ident_number || null,
              document_ident_type: employeeForm.document_ident_type || null,
              document_issuer_service: employeeForm.document_issuer_service || null,
              bi_number: employeeForm.bi_number || null,
            });
          }
          if (selectedProfile.type === 'custom' && createdUserResponse?.data?.id) {
            await rbacAPI.assignRoleToUser(createdUserResponse.data.id, selectedProfile.profileId, 'Atribuição automática do perfil escolhido no cadastro de colaborador');
          }
        }
        toast.success('Colaborador criado');
      }
      setEmployeeDialogOpen(false);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao guardar colaborador'));
    } finally {
      setSaving(false);
    }
  };

  const openCreateRole = () => {
    setEditingRole(null);
    setRoleForm(emptyRoleForm);
    setRoleDialogOpen(true);
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name || '',
      description: role.description || '',
      color: role.color || '#1d4ed8',
      permissions: normalizeProfilePermissions(role.permissions || [], permissions),
    });
    setRoleDialogOpen(true);
  };

  const togglePermission = (permissionSlug) => {
    if (editingRole?.type === 'base' && editingRole?.key === 'admin') return;
    setRoleForm((current) => ({
      ...current,
      permissions: current.permissions.includes(permissionSlug)
        ? current.permissions.filter((item) => item !== permissionSlug)
        : [...current.permissions, permissionSlug],
    }));
  };

  const setAllRolePermissions = (shouldSelectAll) => {
    if (editingRole?.type === 'base' && editingRole?.key === 'admin') return;
    setRoleForm((current) => ({
      ...current,
      permissions: shouldSelectAll ? allPermissionSlugs : [],
    }));
  };

  const setModulePermissions = (moduleKey, shouldSelectAll) => {
    if (editingRole?.type === 'base' && editingRole?.key === 'admin') return;
    const module = permissionModules.find((item) => item.key === moduleKey);
    if (!module) return;
    const moduleSlugs = module.permissions.map((permission) => permission.slug);

    setRoleForm((current) => {
      const currentSet = new Set(current.permissions);
      moduleSlugs.forEach((slug) => {
        if (shouldSelectAll) currentSet.add(slug);
        else currentSet.delete(slug);
      });
      return {
        ...current,
        permissions: [...currentSet].sort((left, right) => left.localeCompare(right, 'pt')),
      };
    });
  };

  const saveRole = async () => {
    try {
      setSaving(true);
      const payload = {
        ...roleForm,
        permissions: editingRole?.type === 'base' && editingRole?.key === 'admin'
          ? ['*']
          : roleForm.permissions.filter((permission) => permission !== '*'),
      };
      if (editingRole) {
        if (editingRole.type === 'base') {
          await rbacAPI.updateBaseRole(editingRole.key, payload);
          toast.success('Perfil base actualizado');
        } else {
          await rbacAPI.updateRole(editingRole.id, payload);
          toast.success('Perfil actualizado');
        }
      } else {
        await rbacAPI.createRole(payload);
        toast.success('Perfil criado');
      }
      setRoleDialogOpen(false);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao guardar perfil'));
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async (role) => {
    try {
      if (role.type === 'base') {
        await rbacAPI.deleteBaseRole(role.key);
        toast.success('Perfil base eliminado');
      } else {
        await rbacAPI.deleteRole(role.id);
        toast.success('Perfil eliminado');
      }
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao eliminar perfil'));
    }
  };

  const confirmDeleteEntity = async () => {
    if (!deleteEntityDialog.item || !deleteEntityDialog.type) return;
    const { item, type } = deleteEntityDialog;
    const actionKey = `${type}-delete-${item.id}`;

    try {
      setQuickActionKey(actionKey);
      if (type === 'user') {
        await usersAPI.delete(item.id, 'Apagado pelo Centro Administrativo');
        toast.success('Utilizador apagado com sucesso');
      } else if (type === 'employee') {
        await settingsAPI.deleteEmployee(item.id, 'Apagado pelo Centro Administrativo');
        toast.success('Colaborador apagado com sucesso');
      }
      setDeleteEntityDialog({ open: false, type: null, item: null });
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, `Erro ao apagar ${type === 'user' ? 'utilizador' : 'colaborador'}`));
    } finally {
      setQuickActionKey(null);
    }
  };

  const assignRoleToUser = async (userId, roleId) => {
    try {
      await rbacAPI.assignRoleToUser(userId, roleId === '__none__' ? null : roleId, 'Atribuição inline via Centro Administrativo');
      toast.success('Perfil actualizado no utilizador');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao atribuir perfil'));
    }
  };

  const assignUnifiedProfileToUser = async (user, profileValue) => {
    const selectedProfile = parseUserProfileValue(profileValue);
    try {
      setQuickActionKey(`user-profile-${user.id}`);
      if (selectedProfile.type === 'base') {
        if ((user.role || 'staff') !== selectedProfile.key) {
          await usersAPI.update(user.id, {
            role: selectedProfile.key,
            justification: 'Sincronização inline de perfil via Centro Administrativo',
          });
        }
        if (user.custom_role_id) {
          await rbacAPI.assignRoleToUser(user.id, null, 'Remoção do perfil criado via selector unificado');
        }
      } else {
        await rbacAPI.assignRoleToUser(user.id, selectedProfile.id, 'Atribuição inline via selector unificado de perfil');
      }
      toast.success('Perfil actualizado no utilizador');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao actualizar perfil do utilizador'));
    } finally {
      setQuickActionKey(null);
    }
  };

  const toggleUserSuspension = async (user) => {
    const nextStatus = user.status === 'active' ? 'suspended' : 'active';
    try {
      setQuickActionKey(`user-status-${user.id}`);
      await usersAPI.update(user.id, {
        status: nextStatus,
        justification: nextStatus === 'suspended'
          ? 'Suspensão rápida via cockpit do Centro Administrativo'
          : 'Reactivação rápida via cockpit do Centro Administrativo',
      });
      toast.success(nextStatus === 'suspended' ? 'Utilizador suspenso' : 'Utilizador reactivado');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro na acção rápida do utilizador'));
    } finally {
      setQuickActionKey(null);
    }
  };

  const toggleEmployeeStatus = async (employee) => {
    try {
      setQuickActionKey(`employee-status-${employee.id}`);
      await settingsAPI.updateEmployee(employee.id, {
        is_active: !(employee.is_active ?? true),
        user_id: employee.user_id || getLinkedUserForEmployee(employee)?.id || null,
      }, employee.is_active ? 'Desactivação rápida via cockpit do Centro Administrativo' : 'Reactivação rápida via cockpit do Centro Administrativo');
      toast.success(employee.is_active ? 'Colaborador desactivado' : 'Colaborador reactivado');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro na acção rápida do colaborador'));
    } finally {
      setQuickActionKey(null);
    }
  };

  const toggleUserSelection = (userId, checked) => {
    setSelectedUserIds((current) => (
      checked ? [...new Set([...current, userId])] : current.filter((id) => id !== userId)
    ));
  };

  const toggleEmployeeSelection = (employeeId, checked) => {
    setSelectedEmployeeIds((current) => (
      checked ? [...new Set([...current, employeeId])] : current.filter((id) => id !== employeeId)
    ));
  };

  const toggleAllVisibleUsers = (checked) => {
    setSelectedUserIds(checked ? filteredUsers.map((item) => item.id) : []);
  };

  const toggleAllVisibleEmployees = (checked) => {
    setSelectedEmployeeIds(checked ? filteredEmployees.map((item) => item.id) : []);
  };

  const handleBulkUserStatus = async (action) => {
    if (!selectedUserIds.length) {
      toast.error('Seleccione utilizadores para a ação em massa');
      return;
    }

    try {
      setSaving(true);
      const labels = {
        suspend: 'Suspensão em massa via Centro Administrativo',
        activate: 'Reactivação em massa via Centro Administrativo',
        deactivate: 'Desactivação em massa via Centro Administrativo',
      };
      const response = await usersAPI.bulkStatus({ ids: selectedUserIds, action, justification: labels[action] || 'Acção em massa' });
      toast.success(response.data?.message || 'Ação em massa concluída');
      setSelectedUserIds([]);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro na ação em massa dos utilizadores'));
    } finally {
      setSaving(false);
    }
  };

  const handleBulkEmployeeStatus = async (action) => {
    if (!selectedEmployeeIds.length) {
      toast.error('Seleccione colaboradores para a ação em massa');
      return;
    }

    try {
      setSaving(true);
      const labels = {
        activate: 'Reactivação em massa via Centro Administrativo',
        deactivate: 'Desactivação em massa via Centro Administrativo',
      };
      const response = await settingsAPI.bulkEmployeeStatus({ ids: selectedEmployeeIds, action, justification: labels[action] || 'Acção em massa' });
      toast.success(response.data?.message || 'Ação em massa concluída');
      setSelectedEmployeeIds([]);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro na ação em massa dos colaboradores'));
    } finally {
      setSaving(false);
    }
  };

  const toggleUserExportColumn = (columnKey) => {
    setUserExportColumns((current) => current.includes(columnKey) ? current.filter((item) => item !== columnKey) : [...current, columnKey]);
  };

  const toggleEmployeeExportColumn = (columnKey) => {
    setEmployeeExportColumns((current) => current.includes(columnKey) ? current.filter((item) => item !== columnKey) : [...current, columnKey]);
  };

  const handleExportUsersXlsx = async () => {
    if (!selectedUserIds.length) {
      toast.error('Seleccione utilizadores para exportar');
      return;
    }

    try {
      const response = await usersAPI.exportUsersXlsx({ ids: selectedUserIds.join(','), columns: userExportColumns.join(',') });
      downloadBlobFile(response.data, `utilizadores_${new Date().toISOString().slice(0, 10)}.xlsx`);
      setUserExportDialogOpen(false);
      toast.success('XLSX de utilizadores gerado');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao exportar utilizadores'));
    }
  };

  const handleExportEmployeesXlsx = async () => {
    if (!selectedEmployeeIds.length) {
      toast.error('Seleccione colaboradores para exportar');
      return;
    }

    try {
      const response = await settingsAPI.exportEmployeesXlsx({ ids: selectedEmployeeIds.join(','), columns: employeeExportColumns.join(',') });
      downloadBlobFile(response.data, `colaboradores_${new Date().toISOString().slice(0, 10)}.xlsx`);
      setEmployeeExportDialogOpen(false);
      toast.success('XLSX de colaboradores gerado');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao exportar colaboradores'));
    }
  };

  const persistEmployeeLink = async (user, employee) => {
    if (!user || !employee) return;
    try {
      setQuickActionKey(`link-${user.id}-${employee.id}`);
      await settingsAPI.updateEmployee(employee.id, {
        user_id: user.id,
      }, 'Vínculo persistente Utilizador↔Colaborador via Centro Administrativo');
      toast.success('Vínculo persistente actualizado');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao persistir vínculo'));
    } finally {
      setQuickActionKey(null);
    }
  };

  const openResetPassword = (user) => {
    setResetTargetUser(user);
    setPasswordForm({ new_password: '', justification: '' });
    setPasswordDialogOpen(true);
  };

  const openUserAuditDialog = async (user) => {
    try {
      setSelectedUserAudit(user);
      setAuditLoading(true);
      setUserAuditDialogOpen(true);
      const response = await usersAPI.getAuditLog(user.id);
      setSelectedUserAuditLogs(response.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar trilho do utilizador'));
      setSelectedUserAuditLogs([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const savePasswordReset = async () => {
    if (!resetTargetUser) return;
    try {
      setSaving(true);
      await usersAPI.resetPassword(resetTargetUser.id, passwordForm.new_password, passwordForm.justification || 'Reset inline via Centro Administrativo');
      toast.success('Password redefinida com sucesso');
      setPasswordDialogOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao redefinir password'));
    } finally {
      setSaving(false);
    }
  };

  if (!canAccess) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="h-5 w-5" /> Acesso Restrito</CardTitle>
            <CardDescription>Este módulo é reservado para administradores e gerentes autorizados.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-slate-100 p-3 space-y-3" data-testid="admin-center-page">
      <div className="flex flex-col gap-2 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Centro Administrativo</h1>
          <p className="mt-1 max-w-3xl text-xs text-muted-foreground">
            Cockpit operacional completo para criar, editar, vincular e auditar Utilizadores, Colaboradores, Perfis e gaps de permissões sem sair desta área.
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="admin-center-actions">
          <Button className="h-8 rounded-sm px-3 text-xs" onClick={openCreateUser} data-testid="admin-center-create-user"><Plus className="mr-1.5 h-3.5 w-3.5" /> Adicionar utilizador</Button>
          <Button className="h-8 rounded-sm px-3 text-xs" variant="outline" onClick={openCreateRole} data-testid="admin-center-create-role"><Plus className="mr-1.5 h-3.5 w-3.5" /> Adicionar perfil</Button>
          <Button className="h-8 rounded-sm px-3 text-xs" variant="ghost" onClick={loadData} data-testid="admin-center-refresh"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Actualizar</Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <Card data-testid="admin-center-summary-users"><CardContent className="px-4 py-4"><p className="text-[11px] uppercase tracking-wide text-muted-foreground">Utilizadores</p><p className="mt-1 text-2xl font-semibold">{users.length}</p></CardContent></Card>
        <Card data-testid="admin-center-summary-roles"><CardContent className="px-4 py-4"><p className="text-[11px] uppercase tracking-wide text-muted-foreground">Perfis configuráveis</p><p className="mt-1 text-2xl font-semibold">{roles.length}</p></CardContent></Card>
        <Card data-testid="admin-center-summary-employees"><CardContent className="px-4 py-4"><p className="text-[11px] uppercase tracking-wide text-muted-foreground">Colaboradores</p><p className="mt-1 text-2xl font-semibold">{employees.length}</p></CardContent></Card>
        <Card data-testid="admin-center-summary-linked"><CardContent className="px-4 py-4"><p className="text-[11px] uppercase tracking-wide text-muted-foreground">Ligados por email</p><p className="mt-1 text-2xl font-semibold">{insights.linkedUsers.length}</p></CardContent></Card>
        <Card data-testid="admin-center-summary-permission-gaps"><CardContent className="px-4 py-4"><p className="text-[11px] uppercase tracking-wide text-muted-foreground">Gaps RBAC</p><p className="mt-1 text-2xl font-semibold">{(coverage?.summary?.yellow || 0) + (coverage?.summary?.red || 0)}</p></CardContent></Card>
      </div>

      <Card data-testid="admin-center-governance-card" className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Link2 className="h-4 w-4 text-sky-600" /> Vinculação automática e alertas de permissões</CardTitle>
          <CardDescription>As ligações Utilizador↔Colaborador são detectadas automaticamente por email e os gaps de RBAC aparecem aqui em tempo real.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3" data-testid="admin-center-gap-users-no-custom-role">
            <p className="text-xs uppercase tracking-wide text-amber-700">Só perfil do sistema</p>
            <p className="mt-2 text-2xl font-semibold text-amber-900">{insights.usersWithoutCustomRole.length}</p>
          </div>
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3" data-testid="admin-center-gap-employees-without-account">
            <p className="text-xs uppercase tracking-wide text-rose-700">Colaboradores sem conta</p>
            <p className="mt-2 text-2xl font-semibold text-rose-900">{insights.employeesWithoutAccount.length}</p>
          </div>
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-3" data-testid="admin-center-gap-users-without-employee">
            <p className="text-xs uppercase tracking-wide text-blue-700">Utilizadores sem ficha</p>
            <p className="mt-2 text-2xl font-semibold text-blue-900">{insights.usersWithoutEmployee.length}</p>
          </div>
          <div className="rounded-2xl border border-orange-200 bg-orange-50 p-3" data-testid="admin-center-gap-rbac-coverage">
            <p className="text-xs uppercase tracking-wide text-orange-700">Fallback/rotas públicas</p>
            <p className="mt-2 text-2xl font-semibold text-orange-900">{(coverage?.summary?.yellow || 0) + (coverage?.summary?.red || 0)}</p>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="admin-center-cockpit-toolbar">
        <CardContent className="pt-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:items-center">
              <div className="relative min-w-[260px] flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={directoryQuery}
                  onChange={(event) => setDirectoryQuery(event.target.value)}
                  placeholder="Pesquisar utilizadores, colaboradores, emails, perfis..."
                  className="pl-9"
                  data-testid="admin-center-search-input"
                />
              </div>

              <Select value={directoryStatusFilter} onValueChange={setDirectoryStatusFilter}>
                <SelectTrigger className="w-full lg:w-[190px]" data-testid="admin-center-status-filter">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos estados</SelectItem>
                  <SelectItem value="active">Activos</SelectItem>
                  <SelectItem value="inactive">Inactivos</SelectItem>
                  <SelectItem value="suspended">Suspensos</SelectItem>
                </SelectContent>
              </Select>

              <Select value={linkageFilter} onValueChange={setLinkageFilter}>
                <SelectTrigger className="w-full lg:w-[220px]" data-testid="admin-center-linkage-filter">
                  <SelectValue placeholder="Ligação" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas leituras</SelectItem>
                  <SelectItem value="linked">Com ligação detectada</SelectItem>
                  <SelectItem value="persisted">Ligação persistente</SelectItem>
                  <SelectItem value="unlinked">Sem ligação</SelectItem>
                  <SelectItem value="role-gap">Gap de perfil/permissões</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-wrap gap-2 text-xs" data-testid="admin-center-cockpit-summary">
              <Badge variant="outline" className="rounded-full bg-white">Vínculos persistentes: {cockpitSummary.linkedPersistent}</Badge>
              <Badge variant="outline" className="rounded-full bg-white">Utilizadores suspensos: {cockpitSummary.suspendedUsers}</Badge>
              <Badge variant="outline" className="rounded-full bg-white">Colaboradores inactivos: {cockpitSummary.inactiveEmployees}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="admin-visibility-diagnostics-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Eye className="h-4 w-4 text-indigo-600" />
            Visibilidade e Diagnóstico
          </CardTitle>
          <CardDescription>
            Estado explícito de flags/filtros e comportamentos ocultos que impactam operação diária.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2" data-testid="admin-visibility-diagnostics-grid">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
            <p className="font-medium text-slate-900">Flags de impressão</p>
            <ul className="mt-2 space-y-1 text-slate-600">
              <li>• auto_print_pos (por localização)</li>
              <li>• print_copies</li>
              <li>• show_signature_line / show_qr_code</li>
            </ul>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
            <p className="font-medium text-slate-900">Filtros contextuais activos</p>
            <ul className="mt-2 space-y-1 text-slate-600">
              <li>• tab, q, location_id, status (via query params)</li>
              <li>• módulos: Vendas/Caixa, Documentos, AR, Compras, Transferências, Serviços</li>
            </ul>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
            <p className="font-medium text-slate-900">Estados ocultos por padrão</p>
            <ul className="mt-2 space-y-1 text-slate-600">
              <li>• Registos soft-deleted não visíveis por defeito</li>
              <li>• Sessões/itens filtrados por localização activa</li>
            </ul>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
            <p className="font-medium text-slate-900">Marcas visuais controladas</p>
            <ul className="mt-2 space-y-1 text-slate-600">
              <li>• Badge “NOVO” removido do Admin Center e Relatórios</li>
              <li>• Label “Produção” removida da UI principal</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={handleAdminCenterTabChange} className="space-y-3">
        <TabsList className="flex flex-wrap gap-1 rounded-sm bg-slate-200/70 p-1" data-testid="admin-center-tabs">
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="users" data-testid="admin-center-tab-users">Utilizadores</TabsTrigger>
            <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="employees" data-testid="admin-center-tab-employees">Colaboradores</TabsTrigger>
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="roles" data-testid="admin-center-tab-roles">Perfis</TabsTrigger>
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="gaps" data-testid="admin-center-tab-gaps">Alertas</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <Card data-testid="admin-center-users-card">
            <CardHeader className="space-y-4">
              <div>
                <CardTitle className="flex items-center gap-2 text-base"><Users className="h-4 w-4 text-blue-600" /> Utilizadores</CardTitle>
                <CardDescription>Contas do sistema, perfis base e trilho de alterações com leitura mais clara e controlo administrativo no mesmo topo visual.</CardDescription>
              </div>

              <div className="grid gap-3 lg:grid-cols-[minmax(0,1.5fr)_minmax(280px,0.9fr)]">
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="admin-center-users-top-summary">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Activos</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-900">{usersSummary.active}</p>
                  </div>
                  <div className="rounded-2xl border border-red-200 bg-red-50 p-3">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-red-700">Suspensos</p>
                    <p className="mt-2 text-2xl font-semibold text-red-700">{usersSummary.suspended}</p>
                  </div>
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-amber-700">Só perfil do sistema</p>
                    <p className="mt-2 text-2xl font-semibold text-amber-700">{usersSummary.withoutCustomRole}</p>
                  </div>
                  <div className="rounded-2xl border border-blue-200 bg-blue-50 p-3">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-blue-700">Eventos</p>
                    <p className="mt-2 text-2xl font-semibold text-blue-700">{usersSummary.audits}</p>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid="admin-center-users-audit-rail">
                  <div className="mb-3 flex items-center gap-2">
                    <History className="h-4 w-4 text-slate-600" />
                    <p className="text-sm font-medium text-slate-900">Trilho recente</p>
                  </div>
                  <div className="space-y-2">
                    {userAuditTrail.slice(0, 3).map((entry) => (
                      <div key={entry.id} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
                        <p className="font-medium text-slate-900">{entry.user_email || 'Utilizador'}</p>
                        <p>{entry.action || 'alteração'} • {entry.performed_by_name || 'Sistema'}</p>
                        <p className="text-slate-400">{entry.timestamp ? new Date(entry.timestamp).toLocaleString('pt-PT', { timeZone: 'Africa/Maputo' }) : 'agora'}</p>
                      </div>
                    ))}
                    {!userAuditTrail.length && <p className="text-xs text-slate-500">Sem eventos recentes para mostrar.</p>}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid="admin-center-users-bulk-toolbar">
                <Select value={sortMode} onValueChange={setSortMode}>
                  <SelectTrigger className="w-[220px]" data-testid="admin-center-users-sort-select">
                    <SelectValue placeholder="Ordenar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name-asc">Nome A-Z</SelectItem>
                    <SelectItem value="name-desc">Nome Z-A</SelectItem>
                    <SelectItem value="collab-asc">Nº colaborador crescente</SelectItem>
                    <SelectItem value="collab-desc">Nº colaborador decrescente</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={() => toggleAllVisibleUsers(!allVisibleUsersSelected)} data-testid="admin-center-users-select-all-button">
                  {allVisibleUsersSelected ? 'Limpar selecção' : 'Seleccionar visíveis'}
                </Button>
                <Button variant="outline" size="sm" disabled={!selectedUserIds.length || saving} onClick={() => handleBulkUserStatus('suspend')} data-testid="admin-center-users-bulk-suspend-button">
                  <PowerOff className="mr-2 h-4 w-4" /> Suspender ({selectedUserIds.length})
                </Button>
                <Button variant="outline" size="sm" disabled={!selectedUserIds.length || saving} onClick={() => handleBulkUserStatus('activate')} data-testid="admin-center-users-bulk-activate-button">
                  <Power className="mr-2 h-4 w-4" /> Reactivar
                </Button>
                <Button variant="outline" size="sm" disabled={!selectedUserIds.length} onClick={() => setUserExportDialogOpen(true)} data-testid="admin-center-users-export-xlsx-button">
                  <FileSpreadsheet className="mr-2 h-4 w-4" /> XLSX Configurável
                </Button>
              </div>
              {loading && !users.length ? <CompactTabSkeleton rows={3} /> : filteredUsers.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum utilizador corresponde aos filtros actuais.</p> : filteredUsers.map((item) => {
                const linkedEmployee = getLinkedEmployeeForUser(item);
                const isPersistedLink = linkedEmployee?.user_id === item.id;
                return (
                  <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={`admin-center-user-${item.id}`}>
                    <div className="grid gap-4 xl:grid-cols-[1.2fr_240px_240px_220px] xl:items-center">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Checkbox checked={selectedUserIds.includes(item.id)} onCheckedChange={(checked) => toggleUserSelection(item.id, Boolean(checked))} data-testid={`admin-center-user-select-${item.id}`} />
                          <p className="font-medium text-slate-900">{item.full_name}</p>
                          <Badge variant="outline" className={STATUS_COLORS[item.status] || ''}>{item.status}</Badge>
                          <Badge variant={linkedEmployee ? 'default' : 'secondary'}>{linkedEmployee ? (isPersistedLink ? 'Vínculo persistente' : 'Ligado por email') : 'Sem vínculo'}</Badge>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{item.email}</p>
                        <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                          <Badge variant="outline">Colab. {item.employee_number || linkedEmployee?.employee_number || '—'}</Badge>
                          <Badge variant="outline">{resolveUserProfileLabel(item)}</Badge>
                          <Badge variant="outline">{item.store_location || 'Sem loja'}</Badge>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Cargo / Perfil</Label>
                        <Select value={resolveUserProfileValue(item)} onValueChange={(value) => assignUnifiedProfileToUser(item, value)}>
                          <SelectTrigger data-testid={`admin-center-assign-role-${item.id}`} disabled={quickActionKey === `user-profile-${item.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {unifiedUserProfileOptions.map((profile) => <SelectItem key={profile.value} value={profile.value}>{profile.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Vinculação automática</Label>
                        <div className="mt-2 rounded-xl border border-dashed border-slate-300 bg-white px-3 py-2 text-sm text-slate-700">
                          {linkedEmployee ? `Colaborador: ${linkedEmployee.name}${isPersistedLink ? ' · persistido' : ''}` : 'Nenhum colaborador com este email'}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 xl:justify-end">
                        {linkedEmployee && !isPersistedLink && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => persistEmployeeLink(item, linkedEmployee)}
                            disabled={quickActionKey === `link-${item.id}-${linkedEmployee.id}`}
                            data-testid={`admin-center-persist-link-user-${item.id}`}
                          >
                            {quickActionKey === `link-${item.id}-${linkedEmployee.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleUserSuspension(item)}
                          disabled={quickActionKey === `user-status-${item.id}`}
                          data-testid={`admin-center-toggle-user-status-${item.id}`}
                        >
                          {quickActionKey === `user-status-${item.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : item.status === 'active' ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openUserAuditDialog(item)} data-testid={`admin-center-view-user-${item.id}`}><Eye className="h-4 w-4" /></Button>
                        <Button size="sm" variant="outline" onClick={() => openResetPassword(item)} data-testid={`admin-center-reset-password-${item.id}`}>Pwd</Button>
                        <Button size="sm" variant="outline" onClick={() => openEditUser(item)} data-testid={`admin-center-edit-user-${item.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive"
                          onClick={() => setDeleteEntityDialog({ open: true, type: 'user', item })}
                          disabled={quickActionKey === `user-delete-${item.id}`}
                          data-testid={`admin-center-delete-user-${item.id}`}
                        >
                          {quickActionKey === `user-delete-${item.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employees">
          <Card data-testid="admin-center-employees-card">
            <CardHeader className="space-y-2">
              <div>
                <CardTitle className="flex items-center gap-2 text-base"><BriefcaseBusiness className="h-4 w-4 text-violet-600" /> Colaboradores</CardTitle>
                <CardDescription>Gestão inline da equipa, ligação com contas de utilizador e controlo operacional no mesmo cockpit administrativo.</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid="admin-center-employees-bulk-toolbar">
                <Select value={sortMode} onValueChange={setSortMode}>
                  <SelectTrigger className="w-[220px]" data-testid="admin-center-employees-sort-select">
                    <SelectValue placeholder="Ordenar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name-asc">Nome A-Z</SelectItem>
                    <SelectItem value="name-desc">Nome Z-A</SelectItem>
                    <SelectItem value="collab-asc">Nº colaborador crescente</SelectItem>
                    <SelectItem value="collab-desc">Nº colaborador decrescente</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={() => toggleAllVisibleEmployees(!allVisibleEmployeesSelected)} data-testid="admin-center-employees-select-all-button">
                  {allVisibleEmployeesSelected ? 'Limpar selecção' : 'Seleccionar visíveis'}
                </Button>
                <Button variant="outline" size="sm" disabled={!selectedEmployeeIds.length || saving} onClick={() => handleBulkEmployeeStatus('deactivate')} data-testid="admin-center-employees-bulk-deactivate-button">
                  <PowerOff className="mr-2 h-4 w-4" /> Desactivar ({selectedEmployeeIds.length})
                </Button>
                <Button variant="outline" size="sm" disabled={!selectedEmployeeIds.length || saving} onClick={() => handleBulkEmployeeStatus('activate')} data-testid="admin-center-employees-bulk-activate-button">
                  <Power className="mr-2 h-4 w-4" /> Reactivar
                </Button>
                <Button variant="outline" size="sm" disabled={!selectedEmployeeIds.length} onClick={() => setEmployeeExportDialogOpen(true)} data-testid="admin-center-employees-export-xlsx-button">
                  <Download className="mr-2 h-4 w-4" /> XLSX Configurável
                </Button>
              </div>
              {loading && !employees.length ? <CompactTabSkeleton rows={3} /> : filteredEmployees.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum colaborador corresponde aos filtros actuais.</p> : filteredEmployees.map((item) => {
                const linkedUser = getLinkedUserForEmployee(item);
                const hasPersistedLink = Boolean(item.user_id);
                return (
                  <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={`admin-center-employee-${item.id}`}>
                    <div className="grid gap-4 xl:grid-cols-[1.2fr_240px_240px_220px] xl:items-center">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Checkbox checked={selectedEmployeeIds.includes(item.id)} onCheckedChange={(checked) => toggleEmployeeSelection(item.id, Boolean(checked))} data-testid={`admin-center-employee-select-${item.id}`} />
                          <p className="font-medium text-slate-900">{item.name}</p>
                          <Badge variant={linkedUser ? 'default' : 'secondary'}>{linkedUser ? (hasPersistedLink ? 'Ligação persistente' : 'Ligado por email') : 'Sem conta'}</Badge>
                          <Badge variant="outline" className={item.is_active ? 'border-green-300 bg-green-50 text-green-700' : 'border-slate-300 bg-slate-100 text-slate-700'}>{item.is_active ? 'Activo' : 'Inactivo'}</Badge>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{item.email || 'Sem email'}</p>
                        <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                          <Badge variant="outline">Colab. {item.employee_number || '—'}</Badge>
                          <Badge variant="outline">{item.profile_role_label || item.role || 'Sem perfil'}</Badge>
                          <Badge variant="outline">{item.can_sell ? 'Pode vender' : 'Sem vendas'}</Badge>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Ligação detectada</Label>
                        <div className="mt-2 rounded-xl border border-dashed border-slate-300 bg-white px-3 py-2 text-sm text-slate-700">
                          {linkedUser ? `Utilizador: ${linkedUser.full_name}${hasPersistedLink ? ' · persistido' : ''}` : 'Nenhum utilizador com este email'}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Telefone</Label>
                        <div className="mt-2 rounded-xl border border-dashed border-slate-300 bg-white px-3 py-2 text-sm text-slate-700">
                          {item.phone || 'Sem telefone'}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 xl:justify-end">
                        {linkedUser && !hasPersistedLink && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => persistEmployeeLink(linkedUser, item)}
                            disabled={quickActionKey === `link-${linkedUser.id}-${item.id}`}
                            data-testid={`admin-center-persist-link-employee-${item.id}`}
                          >
                            {quickActionKey === `link-${linkedUser.id}-${item.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleEmployeeStatus(item)}
                          disabled={quickActionKey === `employee-status-${item.id}`}
                          data-testid={`admin-center-toggle-employee-status-${item.id}`}
                        >
                          {quickActionKey === `employee-status-${item.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : item.is_active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openEditEmployee(item)} data-testid={`admin-center-edit-employee-${item.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive"
                          onClick={() => setDeleteEntityDialog({ open: true, type: 'employee', item })}
                          disabled={quickActionKey === `employee-delete-${item.id}`}
                          data-testid={`admin-center-delete-employee-${item.id}`}
                        >
                          {quickActionKey === `employee-delete-${item.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="roles">
          <Card data-testid="admin-center-roles-card">
            <CardHeader className="space-y-2">
              <div>
                <CardTitle className="flex items-center gap-2 text-base"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Perfis do sistema</CardTitle>
                <CardDescription>Todos os perfis ficam integrados numa única grelha, organizados por módulos e preparados para ajuste rápido por tipo de utilizador.</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 lg:grid-cols-3" data-testid="admin-center-profiles-summary-cards">
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-emerald-700">Perfis integrados</p>
                  <p className="mt-2 text-2xl font-semibold text-emerald-950">{combinedRoles.length}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Perfis base</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-950">{baseRoles.length}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Perfis criados</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-950">{roles.length}</p>
                </div>
              </div>

              {loading && !combinedRoles.length ? <CompactTabSkeleton rows={3} /> : tabLoading.roles && !permissions.length ? <CompactTabSkeleton rows={2} /> : filteredRoles.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum perfil corresponde aos filtros actuais.</p> : filteredRoles.map((role) => {
                const linkedCount = role.type === 'base'
                  ? (role.assigned_users_count || users.filter((item) => item.role === role.key).length)
                  : assignments.filter((item) => item.custom_role_id === role.id).length;
                const roleTestId = role.type === 'base' ? `admin-center-base-role-${role.key}` : `admin-center-role-${role.id}`;
                const editTestId = role.type === 'base' ? `admin-center-edit-base-role-${role.key}` : `admin-center-edit-role-${role.id}`;
                const deleteTestId = role.type === 'base' ? `admin-center-delete-base-role-${role.key}` : `admin-center-delete-role-${role.id}`;

                return (
                  <div key={`${role.type}-${role.key || role.id}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={roleTestId}>
                    <div className="grid gap-4 xl:grid-cols-[minmax(0,1.25fr)_160px_180px_160px] xl:items-start">
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: role.color || '#1d4ed8' }} />
                          <p className="font-medium text-slate-900">{role.name}</p>
                          <Badge variant="outline" className="border-slate-300 bg-white text-slate-700">{role.profileTypeLabel}</Badge>
                          {role.isFullAccess && <Badge className="bg-slate-900 text-white">Acesso total</Badge>}
                          {!role.isFullAccess && !(role.normalizedPermissions || []).length && <Badge variant="destructive">Sem permissões</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">{role.description || 'Perfil sem descrição.'}</p>
                        <div className="flex flex-wrap gap-2" data-testid={`admin-center-role-modules-${role.key || role.id}`}>
                          {role.activeModules.map((module) => (
                            <Badge key={`${role.key || role.id}-${module.key}`} variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
                              {module.label}
                            </Badge>
                          ))}
                          {!role.activeModules.length && !role.isFullAccess && <span className="text-xs text-muted-foreground">Sem módulos activos</span>}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Permissões</Label>
                        <div className="mt-2 rounded-xl border border-dashed border-slate-300 bg-white px-3 py-2 text-sm text-slate-700">
                          {role.permissionCountResolved}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Utilizadores ligados</Label>
                        <div className="mt-2 rounded-xl border border-dashed border-slate-300 bg-white px-3 py-2 text-sm text-slate-700">
                          {linkedCount}
                        </div>
                      </div>
                      <div className="flex gap-2 xl:justify-end">
                        <Button size="sm" variant="outline" onClick={() => openEditRole(role)} data-testid={editTestId}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="outline" className="text-destructive" onClick={() => deleteRole(role)} data-testid={deleteTestId}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="admin-center-profile-module-summary-table">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Resumo por perfil e módulos</p>
                    <p className="text-xs text-muted-foreground">Vista rápida para ajustar perfis sem perder a noção dos módulos activos.</p>
                  </div>
                </div>
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-[980px] w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-3">Perfil</th>
                        <th className="px-3 py-3">Tipo</th>
                        <th className="px-3 py-3">Permissões</th>
                        <th className="px-3 py-3">Módulos activos</th>
                        <th className="px-3 py-3">Cobertura</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRoles.map((role) => (
                        <tr key={`summary-${role.type}-${role.key || role.id}`} className="border-b last:border-0">
                          <td className="px-3 py-3 font-medium text-slate-900">{role.name}</td>
                          <td className="px-3 py-3 text-slate-600">{role.profileTypeLabel}</td>
                          <td className="px-3 py-3 text-slate-600">{role.permissionCountResolved}</td>
                          <td className="px-3 py-3 text-slate-600">{role.activeModuleCount}</td>
                          <td className="px-3 py-3">
                            <div className="flex flex-wrap gap-1">
                              {role.isFullAccess ? <Badge className="bg-slate-900 text-white">Todos os módulos</Badge> : role.activeModules.map((module) => (
                                <Badge key={`summary-chip-${role.key || role.id}-${module.key}`} variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">
                                  {module.label}
                                </Badge>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="gaps">
          <div className="grid gap-6 xl:grid-cols-[1fr_1fr]">
            <Card data-testid="admin-center-rbac-alerts-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base"><ShieldAlert className="h-4 w-4 text-orange-600" /> Alertas RBAC</CardTitle>
                <CardDescription>Leitura directa do Coverage Panel para priorizar migração dos endpoints legados.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-orange-700">Fallback legado</p>
                  <p className="mt-2 text-2xl font-semibold text-orange-900">{coverage?.summary?.yellow || 0}</p>
                </div>
                <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-red-700">Rotas públicas</p>
                  <p className="mt-2 text-2xl font-semibold text-red-900">{coverage?.summary?.red || 0}</p>
                </div>
                <Button asChild variant="outline"><Link to="/settings?tab=rbac-coverage">Abrir Coverage Panel <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
              </CardContent>
            </Card>

            <Card data-testid="admin-center-linkage-alerts-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base"><UserCog className="h-4 w-4 text-indigo-600" /> Alertas de Interligação</CardTitle>
                <CardDescription>Vínculo automático por email já activo; aqui ficam os casos pendentes.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-slate-700">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="font-medium">Colaboradores sem conta</p>
                  <div className="mt-2 space-y-2">
                    {insights.employeesWithoutAccount.length === 0 ? (
                      <p className="text-muted-foreground">Nenhum gap detectado.</p>
                    ) : insights.employeesWithoutAccount.slice(0, 5).map((item) => (
                      <div key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-white px-3 py-2" data-testid={`admin-center-gap-create-user-${item.id}`}>
                        <span>{item.name}</span>
                        <Button size="sm" variant="outline" onClick={() => openCreateUserFromEmployee(item)}>Criar conta</Button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="font-medium">Utilizadores sem ficha de colaborador</p>
                  <div className="mt-2 space-y-2">
                    {insights.usersWithoutEmployee.length === 0 ? (
                      <p className="text-muted-foreground">Nenhum gap detectado.</p>
                    ) : insights.usersWithoutEmployee.slice(0, 5).map((item) => (
                      <div key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-white px-3 py-2" data-testid={`admin-center-gap-create-employee-${item.id}`}>
                        <span>{item.full_name}</span>
                        <Badge variant="outline" className="bg-white">Criar no RH</Badge>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="font-medium">Perfis sem permissões</p>
                  <p className="mt-1 text-muted-foreground">{insights.rolesWithoutPermissions.map((item) => item.name).join(', ') || 'Nenhum gap detectado.'}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <ExportColumnsDialog
        open={userExportDialogOpen}
        onOpenChange={setUserExportDialogOpen}
        title="Exportar utilizadores para XLSX"
        description="Escolha as colunas que devem entrar no ficheiro dos utilizadores seleccionados."
        columns={USER_EXPORTABLE_COLUMNS}
        selectedColumns={userExportColumns}
        onToggleColumn={toggleUserExportColumn}
        onSelectAll={(checked) => setUserExportColumns(checked ? USER_EXPORTABLE_COLUMNS.map((column) => column.key) : [])}
        onConfirm={handleExportUsersXlsx}
        dataTestId="admin-center-users-export-columns-dialog"
      />

      <ExportColumnsDialog
        open={employeeExportDialogOpen}
        onOpenChange={setEmployeeExportDialogOpen}
        title="Exportar colaboradores para XLSX"
        description="Escolha as colunas que devem entrar no ficheiro dos colaboradores seleccionados."
        columns={EMPLOYEE_EXPORTABLE_COLUMNS}
        selectedColumns={employeeExportColumns}
        onToggleColumn={toggleEmployeeExportColumn}
        onSelectAll={(checked) => setEmployeeExportColumns(checked ? EMPLOYEE_EXPORTABLE_COLUMNS.map((column) => column.key) : [])}
        onConfirm={handleExportEmployeesXlsx}
        dataTestId="admin-center-employees-export-columns-dialog"
      />

      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="max-w-3xl" data-testid="admin-center-user-dialog">
          <DialogHeader><DialogTitle>{editingUser ? 'Editar utilizador' : 'Adicionar utilizador'}</DialogTitle></DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Nome</Label>
              <Input value={userForm.full_name} onChange={(event) => setUserForm((current) => ({ ...current, full_name: event.target.value }))} data-testid="admin-center-user-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={userForm.email} disabled={Boolean(editingUser)} onChange={(event) => setUserForm((current) => ({ ...current, email: event.target.value }))} data-testid="admin-center-user-email" />
            </div>
            <div>
              <Label>Nº Colaborador</Label>
              <Input value={editingUser?.employee_number || getLinkedEmployeeForUser(editingUser || {})?.employee_number || '—'} disabled data-testid="admin-center-user-employee-number" />
            </div>
            <div>
              <Label>Telefone</Label>
              <Input value={userForm.phone_number} onChange={(event) => setUserForm((current) => ({ ...current, phone_number: event.target.value }))} data-testid="admin-center-user-phone" />
            </div>
            <div>
              <Label>Loja</Label>
              <Select value={userForm.store_location || '__none__'} onValueChange={(value) => setUserForm((current) => ({ ...current, store_location: value === '__none__' ? '' : value }))}>
                <SelectTrigger data-testid="admin-center-user-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sem loja</SelectItem>
                  {locations.map((location) => <SelectItem key={location.id} value={location.name}>{location.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
                <Label>Cargo / Perfil</Label>
              <Select value={userForm.selected_profile} onValueChange={(value) => setUserForm((current) => ({
                ...current,
                selected_profile: value,
                role: value.startsWith('base:') ? value.replace('base:', '') : (current.role || 'staff'),
              }))}>
                <SelectTrigger data-testid="admin-center-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {unifiedUserProfileOptions.map((profile) => <SelectItem key={profile.value} value={profile.value}>{profile.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={userForm.status} onValueChange={(value) => setUserForm((current) => ({ ...current, status: value }))}>
                <SelectTrigger data-testid="admin-center-user-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => <SelectItem key={status.key} value={status.key}>{status.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!editingUser && (
              <div>
                <Label>Password inicial</Label>
                <Input type="password" value={userForm.password} onChange={(event) => setUserForm((current) => ({ ...current, password: event.target.value }))} data-testid="admin-center-user-password" />
              </div>
            )}
            {userCanConfigureCommercialPin && (
              <div className="md:col-span-2 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="admin-center-user-commercial-pin-panel">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-amber-950">PIN de autorização comercial</p>
                    <p className="text-xs text-amber-800">Usado para autorizar vendas com prejuízo ou abaixo da margem mínima.</p>
                  </div>
                  <Badge variant="outline" data-testid="admin-center-user-commercial-pin-status">
                    {editingUser?.commercial_authorization_pin_enabled ? 'Configurado' : 'Não configurado'}
                  </Badge>
                </div>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>{editingUser ? 'Novo PIN / actualizar' : 'PIN inicial'}</Label>
                    <Input
                      type="password"
                      value={userForm.commercial_authorization_pin}
                      onChange={(event) => setUserForm((current) => ({ ...current, commercial_authorization_pin: event.target.value }))}
                      placeholder="4 a 8 dígitos"
                      data-testid="admin-center-user-commercial-pin-input"
                    />
                  </div>
                  {editingUser?.commercial_authorization_pin_enabled && (
                    <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-white px-3 py-3">
                      <Switch
                        checked={userForm.clear_commercial_authorization_pin}
                        onCheckedChange={(checked) => setUserForm((current) => ({
                          ...current,
                          clear_commercial_authorization_pin: checked,
                          commercial_authorization_pin: checked ? '' : current.commercial_authorization_pin,
                        }))}
                        data-testid="admin-center-user-commercial-pin-clear-switch"
                      />
                      <div>
                        <p className="text-sm font-medium text-slate-900">Remover PIN actual</p>
                        <p className="text-xs text-slate-600">Ao guardar, este utilizador deixa de poder autorizar vendas por PIN.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {!userCanConfigureCommercialPin && editingUser?.commercial_authorization_pin_enabled && (
              <div className="md:col-span-2 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="admin-center-user-commercial-pin-removal-note">
                Este perfil deixa de estar autorizado para PIN comercial. Ao guardar com este perfil, o PIN actual será removido automaticamente.
              </div>
            )}
          </div>
          <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900" data-testid="admin-center-user-link-preview">
              {matchedEmployeeForUserForm ? `Ligação automática detectada com o colaborador: ${matchedEmployeeForUserForm.name}` : 'Nenhum colaborador com este email neste momento.'}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)}>Cancelar</Button>
            <Button onClick={saveUser} disabled={saving} data-testid="admin-center-save-user">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Guardar utilizador
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={employeeDialogOpen} onOpenChange={setEmployeeDialogOpen}>
        <DialogContent className="max-w-5xl gap-3 p-4" data-testid="admin-center-employee-dialog">
          <DialogHeader><DialogTitle>{editingEmployee ? 'Editar colaborador' : 'Adicionar colaborador'}</DialogTitle></DialogHeader>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <div>
              <Label>Nº Colaborador</Label>
              <Input value={employeeForm.employee_number} onChange={(event) => setEmployeeForm((current) => ({ ...current, employee_number: event.target.value }))} placeholder="001" data-testid="admin-center-employee-number" />
            </div>
            <div>
              <Label>Nome</Label>
              <Input value={employeeForm.name} onChange={(event) => setEmployeeForm((current) => ({ ...current, name: event.target.value }))} data-testid="admin-center-employee-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={employeeForm.email} onChange={(event) => setEmployeeForm((current) => ({ ...current, email: event.target.value }))} data-testid="admin-center-employee-email" />
            </div>
            <div>
              <Label>Telefone</Label>
              <Input value={employeeForm.phone} onChange={(event) => setEmployeeForm((current) => ({ ...current, phone: event.target.value }))} data-testid="admin-center-employee-phone" />
            </div>
            <div>
                  <Label>Cargo / Perfil</Label>
              <Select value={employeeForm.selected_profile} onValueChange={(value) => setEmployeeForm((current) => ({ ...current, selected_profile: value }))}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="admin-center-employee-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {unifiedEmployeeProfileOptions.map((profile) => <SelectItem key={profile.value} value={profile.value}>{profile.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="xl:col-span-3 rounded-sm border border-slate-200 bg-slate-50 p-3" data-testid="admin-center-employee-identity-panel">
              <div className="mb-2">
                <p className="text-sm font-semibold text-slate-900">Identificação e Filiação</p>
                <p className="text-xs text-slate-500">Estrutura ultra compacta para o Dossier Digital, sem alterar o BI actual.</p>
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <Label>Número Documento Ident.</Label>
                  <Input value={employeeForm.bi_number} onChange={(event) => setEmployeeForm((current) => ({ ...current, bi_number: event.target.value, document_ident_number: event.target.value }))} data-testid="admin-center-employee-bi-number" />
                </div>
                <div>
                  <Label>Tipo Doc. Identificação</Label>
                  <Select value={employeeForm.document_ident_type || '__none__'} onValueChange={(value) => setEmployeeForm((current) => ({ ...current, document_ident_type: value === '__none__' ? '' : value }))}>
                    <SelectTrigger className="h-9 rounded-sm" data-testid="admin-center-employee-document-type"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Seleccione</SelectItem>
                      {DOCUMENT_IDENTIFICATION_TYPE_OPTIONS.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Serviço Emissor</Label>
                  <Input value={employeeForm.document_issuer_service} onChange={(event) => setEmployeeForm((current) => ({ ...current, document_issuer_service: event.target.value }))} data-testid="admin-center-employee-document-issuer-service" />
                </div>
                <div>
                  <Label>Estado Civil</Label>
                  <Select value={employeeForm.marital_status || '__none__'} onValueChange={(value) => setEmployeeForm((current) => ({ ...current, marital_status: value === '__none__' ? '' : value }))}>
                    <SelectTrigger className="h-9 rounded-sm" data-testid="admin-center-employee-marital-status"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Seleccione</SelectItem>
                      {MARITAL_STATUS_OPTIONS.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Gênero</Label>
                  <Select value={employeeForm.gender || '__none__'} onValueChange={(value) => setEmployeeForm((current) => ({ ...current, gender: value === '__none__' ? '' : value }))}>
                    <SelectTrigger className="h-9 rounded-sm" data-testid="admin-center-employee-gender"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Seleccione</SelectItem>
                      {GENDER_OPTIONS.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nome do Pai</Label>
                  <Input value={employeeForm.father_name} onChange={(event) => setEmployeeForm((current) => ({ ...current, father_name: event.target.value }))} data-testid="admin-center-employee-father-name" />
                </div>
                <div>
                  <Label>Nome da Mãe</Label>
                  <Input value={employeeForm.mother_name} onChange={(event) => setEmployeeForm((current) => ({ ...current, mother_name: event.target.value }))} data-testid="admin-center-employee-mother-name" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={employeeForm.can_sell} onCheckedChange={(checked) => setEmployeeForm((current) => ({ ...current, can_sell: checked }))} data-testid="admin-center-employee-can-sell" />
              <Label>Pode fazer vendas</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={employeeForm.is_active} onCheckedChange={(checked) => setEmployeeForm((current) => ({ ...current, is_active: checked }))} data-testid="admin-center-employee-active" />
              <Label>Activo</Label>
            </div>
          </div>
          <div className="rounded-sm border border-violet-200 bg-violet-50 p-3 text-xs text-violet-900" data-testid="admin-center-employee-link-preview">
                  {matchedUserForEmployeeForm ? `Ligação automática detectada com o utilizador: ${matchedUserForEmployeeForm.full_name}. O cargo/perfil do colaborador seguirá sempre o perfil activo dessa conta.` : 'Seleccione um cargo/perfil. Já não existem cargos/funções em texto livre no cadastro do colaborador.'}
          </div>
          {!editingEmployee && !matchedUserForEmployeeForm && (
            <div className="space-y-3 rounded-sm border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center gap-3">
                <Switch checked={employeeForm.create_user_twin} onCheckedChange={(checked) => setEmployeeForm((current) => ({ ...current, create_user_twin: checked }))} data-testid="admin-center-employee-create-user-switch" />
                <div>
                  <p className="text-sm font-medium">Criar também conta de utilizador</p>
                  <p className="text-xs text-muted-foreground">Se não existir um utilizador com este email, o cockpit cria automaticamente a conta correspondente.</p>
                </div>
              </div>
              {employeeForm.create_user_twin && (
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <Label>Loja</Label>
                    <Select value={employeeForm.user_store_location || '__none__'} onValueChange={(value) => setEmployeeForm((current) => ({ ...current, user_store_location: value === '__none__' ? '' : value }))}>
                      <SelectTrigger data-testid="admin-center-employee-user-location"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Sem loja</SelectItem>
                        {locations.map((location) => <SelectItem key={location.id} value={location.name}>{location.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Salário base</Label>
                    <Input type="number" value={employeeForm.user_base_salary} onChange={(event) => setEmployeeForm((current) => ({ ...current, user_base_salary: event.target.value }))} data-testid="admin-center-employee-user-salary" />
                  </div>
                  <div className="md:col-span-3">
                    <p className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600" data-testid="admin-center-employee-user-profile-note">
                      A conta será criada com o mesmo perfil seleccionado acima. Se o perfil escolhido for personalizado, a conta recebe o perfil customizado automaticamente após a criação.
                    </p>
                  </div>
                  <div className="md:col-span-3">
                    <Label>Password inicial</Label>
                    <Input type="password" value={employeeForm.user_password} onChange={(event) => setEmployeeForm((current) => ({ ...current, user_password: event.target.value }))} data-testid="admin-center-employee-user-password" />
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmployeeDialogOpen(false)}>Cancelar</Button>
            <Button onClick={saveEmployee} disabled={saving} data-testid="admin-center-save-employee">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Guardar colaborador
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="max-w-5xl" data-testid="admin-center-role-dialog">
          <DialogHeader><DialogTitle>{editingRole ? (editingRole.type === 'base' ? 'Editar perfil base' : 'Editar perfil') : 'Adicionar perfil'}</DialogTitle></DialogHeader>
          <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
            <div className="space-y-4">
              <div>
                <Label>Nome</Label>
                <Input value={roleForm.name} onChange={(event) => setRoleForm((current) => ({ ...current, name: event.target.value }))} data-testid="admin-center-role-name" />
              </div>
              <div>
                <Label>Cor</Label>
                <Input value={roleForm.color} onChange={(event) => setRoleForm((current) => ({ ...current, color: event.target.value }))} data-testid="admin-center-role-color" />
              </div>
              <div>
                <Label>Descrição</Label>
                <Textarea rows={5} value={roleForm.description} onChange={(event) => setRoleForm((current) => ({ ...current, description: event.target.value }))} data-testid="admin-center-role-description" />
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                Permissões seleccionadas: <strong>{editingRole?.type === 'base' && editingRole?.key === 'admin' ? allPermissionSlugs.length : roleForm.permissions.length}</strong>
              </div>
              {editingRole?.type === 'base' && editingRole?.key === 'admin' && (
                <div className="rounded-2xl border border-slate-900 bg-slate-900 p-4 text-sm text-white" data-testid="admin-center-admin-role-full-access-banner">
                  O perfil Admin mantém acesso total ao sistema. Pode editar nome, cor e descrição, mas as permissões ficam sempre completas.
                </div>
              )}
            </div>

            <div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label>Permissões por módulo</Label>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setAllRolePermissions(true)} disabled={editingRole?.type === 'base' && editingRole?.key === 'admin'} data-testid="admin-center-role-select-all-permissions">
                    Seleccionar tudo
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setAllRolePermissions(false)} disabled={editingRole?.type === 'base' && editingRole?.key === 'admin'} data-testid="admin-center-role-clear-all-permissions">
                    Limpar tudo
                  </Button>
                </div>
              </div>
              <ScrollArea className="mt-2 h-[420px] rounded-2xl border border-slate-200 p-4" data-testid="admin-center-role-permissions-scroll">
                <div className="space-y-4">
                  {permissionModules.map((module) => {
                    const selectedCount = module.permissions.filter((permission) => roleForm.permissions.includes(permission.slug)).length;
                    return (
                      <div key={module.key} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-center-role-module-${module.key}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{module.label}</p>
                            <p className="text-xs text-muted-foreground">{module.description}</p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">{selectedCount}/{module.permissions.length}</Badge>
                            <Button type="button" variant="outline" size="sm" onClick={() => setModulePermissions(module.key, true)} disabled={editingRole?.type === 'base' && editingRole?.key === 'admin'} data-testid={`admin-center-role-module-select-all-${module.key}`}>
                              Todos do módulo
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => setModulePermissions(module.key, false)} disabled={editingRole?.type === 'base' && editingRole?.key === 'admin'} data-testid={`admin-center-role-module-clear-${module.key}`}>
                              Limpar módulo
                            </Button>
                          </div>
                        </div>
                        {module.permissions.map((permission) => (
                          <label key={permission.slug} className="flex items-start gap-3 rounded-xl border border-transparent px-2 py-2 hover:border-slate-200 hover:bg-slate-50" data-testid={`admin-center-role-permission-${permission.slug}`}>
                            <Checkbox checked={roleForm.permissions.includes(permission.slug) || (editingRole?.type === 'base' && editingRole?.key === 'admin')} onCheckedChange={() => togglePermission(permission.slug)} disabled={editingRole?.type === 'base' && editingRole?.key === 'admin'} />
                            <div className="space-y-1">
                              <p className="text-sm font-medium">{permission.label}</p>
                              <p className="text-xs text-muted-foreground">{permission.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Cancelar</Button>
            <Button onClick={saveRole} disabled={saving} data-testid="admin-center-save-role">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Guardar perfil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteEntityDialog.open} onOpenChange={(open) => setDeleteEntityDialog({ open, type: open ? deleteEntityDialog.type : null, item: open ? deleteEntityDialog.item : null })}>
        <AlertDialogContent data-testid="admin-center-delete-entity-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar eliminação</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteEntityDialog.type === 'user'
                ? `Vai apagar o utilizador ${deleteEntityDialog.item?.full_name || 'seleccionado'} do Centro Administrativo.`
                : `Vai apagar o colaborador ${deleteEntityDialog.item?.name || 'seleccionado'} do Centro Administrativo.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-center-delete-entity-cancel-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteEntity} data-testid="admin-center-delete-entity-confirm-button">
              Confirmar apagar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={userAuditDialogOpen} onOpenChange={setUserAuditDialogOpen}>
        <DialogContent className="max-w-3xl" data-testid="admin-center-user-audit-dialog">
          <DialogHeader>
            <DialogTitle>Visualizar utilizador e trilho de alterações</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.95fr)]">
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-lg font-semibold text-slate-900">{selectedUserAudit?.full_name || 'Utilizador'}</p>
                  {selectedUserAudit?.status && <Badge variant="outline" className={STATUS_COLORS[selectedUserAudit.status] || ''}>{selectedUserAudit.status}</Badge>}
                </div>
                <div className="mt-3 grid gap-3 text-sm text-slate-600 sm:grid-cols-2">
                  <div><span className="font-medium text-slate-900">Email:</span> {selectedUserAudit?.email || '—'}</div>
                  <div><span className="font-medium text-slate-900">Perfil base interno:</span> {baseRoleLabelMap.get(selectedUserAudit?.role) || selectedUserAudit?.role || '—'}</div>
                  <div><span className="font-medium text-slate-900">Perfil activo:</span> {selectedUserAudit?.custom_role_name || baseRoleLabelMap.get(selectedUserAudit?.role) || selectedUserAudit?.role || 'Sem perfil'}</div>
                  <div><span className="font-medium text-slate-900">Loja:</span> {selectedUserAudit?.store_location || '—'}</div>
                  <div><span className="font-medium text-slate-900">Telefone:</span> {selectedUserAudit?.phone_number || '—'}</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {selectedUserAudit && <Button variant="outline" onClick={() => openEditUser(selectedUserAudit)} data-testid="admin-center-user-audit-edit-button"><Pencil className="mr-2 h-4 w-4" />Editar</Button>}
                {selectedUserAudit && <Button variant="outline" onClick={() => openResetPassword(selectedUserAudit)} data-testid="admin-center-user-audit-password-button">Reset password</Button>}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="mb-3 flex items-center gap-2">
                <History className="h-4 w-4 text-slate-600" />
                <p className="text-sm font-medium text-slate-900">Trilho do utilizador</p>
              </div>
              <ScrollArea className="h-[340px] pr-3">
                <div className="space-y-3">
                  {auditLoading ? <p className="text-sm text-slate-500">A carregar trilho...</p> : selectedUserAuditLogs.length === 0 ? <p className="text-sm text-slate-500">Sem alterações registadas.</p> : selectedUserAuditLogs.map((entry) => (
                    <div key={entry.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm shadow-sm" data-testid={`admin-user-history-row-${entry.id}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">{entry.action || 'alteração'}</Badge>
                        <span className="text-xs text-slate-400">{entry.timestamp ? new Date(entry.timestamp).toLocaleString('pt-PT', { timeZone: 'Africa/Maputo' }) : 'agora'}</span>
                      </div>
                      <p className="mt-2 text-slate-700">{entry.details || entry.justification || 'Sem detalhe adicional'}</p>
                      {entry.performed_by_name && <p className="mt-1 text-xs text-slate-500">Por: {entry.performed_by_name}</p>}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-xl" data-testid="admin-center-password-dialog">
          <DialogHeader><DialogTitle>Redefinir password</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              {resetTargetUser ? `Utilizador: ${resetTargetUser.full_name} • ${resetTargetUser.email}` : 'Seleccionar utilizador'}
            </div>
            <div>
              <Label>Nova password</Label>
              <Input type="password" value={passwordForm.new_password} onChange={(event) => setPasswordForm((current) => ({ ...current, new_password: event.target.value }))} data-testid="admin-center-password-new" />
            </div>
            <div>
              <Label>Justificação</Label>
              <Textarea rows={4} value={passwordForm.justification} onChange={(event) => setPasswordForm((current) => ({ ...current, justification: event.target.value }))} data-testid="admin-center-password-justification" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>Cancelar</Button>
            <Button onClick={savePasswordReset} disabled={saving} data-testid="admin-center-password-save">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Redefinir password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}